import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, X } from 'lucide-react';
import {
  FAMILY_LABELS,
  groupIndicatorsByFamily,
  type StatusIndicatorFamily,
} from './status-indicators';
import { StatusPipGlyph } from './StatusPip';
import { HelpAnchor } from './HelpAnchor';

// StatusLegend — `?` affordance in the top bar that opens a side drawer
// listing every status indicator catalogued in src/design-system/
// status-indicators.ts. Reads from the same source-of-truth as the per-pip
// tooltips so the legend can never drift from the actual rendered visuals.
//
// Spec: docs/logic/workspace/status-indicators.md
// Action: greg-tim-2026-05-18.A4
//
// Why global rather than per-scene:
//   - One discoverable entry point ("the help icon in the top bar") matches
//     how reviewers already think about top-level chrome.
//   - Per-scene `?` buttons would fan out N copies of the catalogue, each at
//     risk of drifting from the central map. The legend is short enough that
//     a single comprehensive drawer is easier to scan than seven scoped ones.
//   - Top bar has the existing `HelpCircle` (replay comment intro) — adding a
//     second `?` next to it groups the discoverability affordances together.

// Order families render in the drawer — matches the spec doc's section order
// so reviewers see the same structure as the source-of-truth document.
const FAMILY_ORDER: StatusIndicatorFamily[] = ['state', 'item', 'fallback'];

export function StatusLegendButton({ tooltipClass = 'tt-below' }: { tooltipClass?: string }) {
  const [open, setOpen] = useState(false);

  // Esc to close — matches HelpAnchor's drawer behaviour.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={`theme-toggle ${tooltipClass}`}
        onClick={() => setOpen(true)}
        aria-label="What do the status indicators mean?"
        data-comment-anchor="StatusLegendButton"
      >
        <CircleHelp size={16} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <StatusLegendDrawer onClose={() => setOpen(false)} />,
          document.body,
        )}
    </>
  );
}

function StatusLegendDrawer({ onClose }: { onClose: () => void }) {
  const groups = groupIndicatorsByFamily();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Status indicator legend"
      data-comment-anchor="StatusLegend"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--scrim)',
          backdropFilter: 'blur(2px)',
        }}
      />
      <aside
        style={{
          position: 'relative',
          width: 'min(480px, 92vw)',
          height: '100%',
          background: 'var(--surface-1)',
          borderLeft: '1px solid var(--hairline-strong)',
          boxShadow: '-8px 0 24px var(--shadow-warm-lg)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid var(--hairline)',
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 600,
                color: 'var(--ink-tertiary)',
                marginBottom: '2px',
              }}
            >
              Legend
            </div>
            <div
              style={{
                fontSize: '14px',
                color: 'var(--ink-primary)',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Status indicators
              <HelpAnchor
                doc="workspace/status-indicators"
                label="Status indicators catalogue"
              />
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: 'transparent',
              color: 'var(--ink-secondary)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </header>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 22px 32px',
          }}
        >
          <p
            style={{
              fontSize: '12.5px',
              color: 'var(--ink-secondary)',
              lineHeight: 1.55,
              margin: '0 0 18px',
            }}
          >
            The shared vocabulary every pip, glyph, and meter colour reads from.
            A green dot means the same thing on the dashboard as it does in the
            chapter list as it does on a gates strip. Hover any indicator to
            see its short label; the descriptions below add context.
          </p>

          {FAMILY_ORDER.map((family) => {
            const indicators = groups[family];
            if (indicators.length === 0) return null;
            return (
              <section
                key={family}
                style={{ marginBottom: '22px' }}
                aria-label={FAMILY_LABELS[family]}
              >
                <h3
                  style={{
                    margin: '0 0 10px',
                    fontSize: '10.5px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    fontWeight: 600,
                    color: 'var(--ink-tertiary)',
                  }}
                >
                  {FAMILY_LABELS[family]}
                </h3>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {indicators.map((ind) => (
                    <li
                      key={ind.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr',
                        gap: '12px',
                        alignItems: 'baseline',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        background: 'var(--surface-2)',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '18px',
                        }}
                      >
                        <StatusPipGlyph indicator={ind} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: 'var(--ink-primary)',
                            }}
                          >
                            {ind.label}
                          </span>
                          <span
                            className="tabular"
                            style={{
                              fontSize: '10px',
                              color: 'var(--ink-tertiary)',
                              letterSpacing: '0.02em',
                            }}
                          >
                            {ind.id}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--ink-secondary)',
                            lineHeight: 1.5,
                            marginTop: '2px',
                          }}
                        >
                          {ind.description}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          <p
            style={{
              fontSize: '11px',
              color: 'var(--ink-tertiary)',
              lineHeight: 1.55,
              margin: '24px 0 0',
              paddingTop: '16px',
              borderTop: '1px solid var(--hairline)',
            }}
          >
            Source-of-truth:{' '}
            <a
              href="/docs/logic/workspace/status-indicators"
              style={{
                color: 'var(--ink-secondary)',
                textDecoration: 'underline',
                textDecorationColor: 'var(--hairline)',
              }}
            >
              docs/logic/workspace/status-indicators
            </a>
          </p>
        </div>
      </aside>
    </div>
  );
}
